import { gql, useQuery } from '@apollo/client';

/*
 * Users that can be selected as the validator (verifier) for the "My Events" filter: active
 * accounts, so the list is not padded with people who left.
 * Fetched via getUsers with role = 1 and NO pagination — the service only paginates when
 * a `pagination` arg is present, so omitting it returns the full set (getUsersPaginated
 * caps at its page size). There is no full_name field, so the display name is composed
 * from first_name + last_name by the consumer.
 */

export interface Verifier {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

const GET_VERIFIERS = gql`
  query ValidatorUsers($input: GetUsersInput) {
    getUsers(input: $input) {
      id
      first_name
      last_name
      email
    }
  }
`;

interface QueryResult {
  getUsers?: (Verifier | null)[] | null;
}

interface QueryVars {
  input: {
    role: number;
    active: boolean;
    /* Accepted by getUsers and left optional: see the note on the variables below. */
    command_center_access?: boolean;
  };
}

/* Role id for the users that can validate events. */
const VERIFIER_ROLE = 1;

export const useVerifiers = (): Verifier[] => {
  const { data } = useQuery<QueryResult, QueryVars>(GET_VERIFIERS, {
    /*
     * `active` rather than `status: 2`: the backend owns that mapping, and spelling the number
     * here would plant a constant nobody can trace later. It is ignored when falsy — the service
     * tests it with a plain `if` — so it can only narrow the list, never widen it.
     *
     * `command_center_access: true` belongs here too and is deliberately not sent for now: it
     * would also drop anyone whose account simply has the flag unset, and that is a data problem
     * to look at before it becomes a filter. Add it back to the object to turn it on.
     */
    variables: { input: { role: VERIFIER_ROLE, active: true } },
    fetchPolicy: 'cache-first'
  });

  return (data?.getUsers ?? []).filter((u): u is Verifier => Boolean(u?.email));
};
